import React, { useState, useCallback, Suspense, lazy, useEffect } from 'react';
import './App.css';
import Header from './components/Header';
import Footer from './components/Footer';
import SEO from './components/SEO';
import FloatingConnectBtn from './components/FloatingConnectBtn';
import LoginPage from './features/Auth/LoginPage';
import { fetchSingleAd } from './services/api';
import { parseRoute, getCanonicalUrl } from './utils/routeHelper';

const getInitialAdId = () => {
  const rawPath = window.location.pathname;
  if (!rawPath || rawPath === '/') return null;
  const parsed = parseRoute(rawPath);
  if (parsed) return null; // recognized route is never an ad ID

  const adMatch = rawPath.match(/^\/ad\/([^/]+)$/i);
  if (adMatch) return adMatch[1];

  const directMatch = rawPath.match(/^\/([^/]+)$/i);
  if (directMatch) return directMatch[1];

  return null;
};

const Home = lazy(() => import('./features/Marketplace/pages/Home'));
const SettingsView = lazy(() => import('./features/Settings/SettingsView'));

function App() {
  const initialRoute = parseRoute(window.location.pathname);
  const initialAdId = initialRoute ? null : getInitialAdId();
  const [currentView, setCurrentView] = useState(() => {
    if (initialAdId) return 'loading-ad';
    return initialRoute ? initialRoute.view : 'connect';
  });
  const [currentSubView, setCurrentSubView] = useState(() => {
    return initialRoute ? initialRoute.subView : null;
  });
  const [activeTargetId, setActiveTargetId] = useState(initialAdId);
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem("pondy_user");
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [refreshKey, setRefreshKey] = useState(0);

  const triggerRefresh = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);

  // Resolve deep-linked ad details on mount or popstate
  useEffect(() => {
    const resolveAdRoute = async (adId) => {
      try {
        const res = await fetchSingleAd(adId);
        if (res.success && res.data) {
          const ad = res.data;
          const cat = String(ad.Category || ad.category || '').toLowerCase();
          const coll = String(ad.Collection || ad.collection || '').toLowerCase();
          const isSlotify = cat.includes('slotify') || coll.includes('slotify') || 
                            !!(ad.Booking_Type || ad.Slot_Duration || ad.Open_Slot || ad.Booking_Interval);
          const isRealEstate = cat.includes('real estate') || coll.includes('real estate');
          
          if (isSlotify) {
            setCurrentView('slotify-booking');
            setActiveTargetId(ad);
          } else if (isRealEstate) {
            setCurrentView('real-estate');
            setActiveTargetId(adId);
          } else {
            setCurrentView('market-square');
            setActiveTargetId(adId);
          }
        } else {
          setCurrentView('connect');
          setActiveTargetId(null);
        }
      } catch (err) {
        console.error("Error fetching direct ad:", err);
        setCurrentView('connect');
        setActiveTargetId(null);
      }
    };

    if (currentView === 'loading-ad' && activeTargetId) {
      resolveAdRoute(activeTargetId);
    }
  }, [currentView, activeTargetId]);

  // Handle popstate browser buttons
  useEffect(() => {
    const handlePopState = () => {
      const rawPath = window.location.pathname || '';
      const route = parseRoute(rawPath);

      if (route) {
        setCurrentView(route.view);
        setCurrentSubView(route.subView);
        setActiveTargetId(null);
        return;
      }

      const adId = getInitialAdId();
      if (adId) {
        setCurrentView('loading-ad');
        setActiveTargetId(adId);
      } else {
        setCurrentView('connect');
        setCurrentSubView(null);
        setActiveTargetId(null);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const handleNavigate = useCallback((view, id = null, subView = null) => {
    if (view === 'slotify' && id) {
      view = 'slotify-booking';
    }

    const isSameView = currentView === view && currentSubView === subView;
    const scrollBehavior = isSameView ? 'smooth' : 'auto';

    // Clicked active nav button again or navigated to a new view -> Reset scroll!
    window.scrollTo({ top: 0, behavior: scrollBehavior });
    document.documentElement.scrollTo({ top: 0, behavior: scrollBehavior });
    document.body.scrollTo({ top: 0, behavior: scrollBehavior });

    // Scroll any inner scrollable containers in individual views to the top
    const scrollableSelectors = [
      '.home_content', '.feed', '.connect-wall', '.market-square-view', '.real-estate-view', '.slotify-view', 
      '.app-body', '.connect-wall-container', '.post-feed', '.feed-container', '.posts-scroll',
      '.market-square-container', '.real-estate-container', '.slotify-container', '.settings-view-container'
    ];
    
    // Immediate scroll reset
    scrollableSelectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(el => {
        el.scrollTo({ top: 0, behavior: scrollBehavior });
      });
    });

    // Reset on next tick to ensure any dynamic rendering is captured
    setTimeout(() => {
      scrollableSelectors.forEach(selector => {
        document.querySelectorAll(selector).forEach(el => {
          el.scrollTo({ top: 0, behavior: scrollBehavior });
        });
      });
    }, 0);

    setCurrentView(view);
    setCurrentSubView(subView);
    setActiveTargetId(id);

    // URL management
    if (id) {
      const cleanId = typeof id === 'object' ? (id.Retail_ID || id.RetialID) : id;
      window.history.pushState({ view, id: cleanId }, '', `/ad/${cleanId}`);
    } else {
      const canonicalUrl = getCanonicalUrl(view, subView);
      window.history.pushState({ view, subView }, '', canonicalUrl);
    }
  }, [currentView, currentSubView]);

  const rawPath = window.location.pathname || '';
  const normPath = rawPath.toLowerCase().replace(/\/+$/, '');
  const isPolicyRoute = normPath === '/privacy-policy' || normPath === '/child-safety' || normPath === '/terms-and-conditions' || normPath === '/delete-account';

  if (isPolicyRoute) {
    let pageTitle = 'Policies - Pondy Connect';
    let policyType = 'privacy';
    if (normPath === '/privacy-policy') {
      pageTitle = 'Privacy Policy - Pondy Connect';
      policyType = 'privacy';
    } else if (normPath === '/child-safety') {
      pageTitle = 'Child Safety Standards - Pondy Connect';
      policyType = 'safety';
    } else if (normPath === '/terms-and-conditions') {
      pageTitle = 'Terms & Conditions - Pondy Connect';
      policyType = 'terms';
    } else if (normPath === '/delete-account') {
      pageTitle = 'Delete Account - Pondy Connect';
      policyType = 'delete';
    }

    return (
      <div className="app-policy-standalone" style={{ padding: '20px', maxWidth: '900px', margin: '0 auto', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <SEO 
          title={pageTitle} 
          description="Pondy Connect policies and standards." 
        />
        <Suspense fallback={<div className="loading-fallback"><div className="loading-fallback-ring"></div>Loading...</div>}>
          <SettingsView 
            type={policyType} 
            user={null}
            onNavigate={handleNavigate} 
          />
        </Suspense>
      </div>
    );
  }

  const isPublicSettingsRoute = 
    currentView === 'download' || 
    (currentView === 'settings' && ['about', 'business', 'guide', 'privacy', 'terms', 'safety', 'delete', 'download'].includes(currentSubView));

  if (!user && !isPublicSettingsRoute) {
    return (
      <div className="app-auth-wrapper">
        <SEO title="Login - Pondy Connect" description="Log in to your Pondy Connect account." />
        <LoginPage onLogin={setUser} />
      </div>
    );
  }

  if (currentView === 'loading-ad') {
    return (
      <div className="loading-fallback">
        <SEO title="Loading Ad - Pondy Connect" description="Loading the requested advertisement..." />
        <div className="loading-fallback-ring"></div>
        <p style={{ marginTop: '16px', fontSize: '14px', fontWeight: '500', color: '#64748b' }}>
          Loading Advertisement...
        </p>
      </div>
    );
  }

  return (
    <div className="app">
      <SEO
        title={currentView === 'connect' ? 'Pondy Connect - Home' :
          currentView === 'download' ? 'Download Mobile App - Pondy Connect' :
          currentView === 'market-square' ? 'Discover - Pondy Connect' :
            currentView === 'slotify' ? 'Appointment Neural Grid' :
              currentView === 'booster' ? 'Booster Reach' :
                currentView === 'real-estate' ? 'Property - Pondy Connect' :
                    'Pondy Connect'}
        description="The premier classifieds and community platform for Pondicherry. Download the Pondy Connect app on Google Play and Apple App Store."
      />
      <Header
        currentView={currentView}
        onNavigate={handleNavigate}
        user={user}
        onRefresh={triggerRefresh}
      />

      <Suspense fallback={<div className="loading-fallback"><div className="loading-fallback-ring"></div>Loading Experience...</div>}>
        <Home
          currentView={currentView}
          subView={currentSubView}
          onNavigate={handleNavigate}
          user={user}
          setUser={setUser}
          onRefresh={triggerRefresh}
          activeTargetId={activeTargetId}
        />
      </Suspense>

      <Footer onNavigate={handleNavigate} />

      {currentView !== 'download' && !['settings', 'about', 'business', 'guide', 'privacy', 'terms', 'safety', 'blocked', 'following', 'events', 'profile'].includes(currentView) && (
        <FloatingConnectBtn 
          onClick={() => handleNavigate(`post-${currentView}`)} 
          view={currentView}
        />
      )}
    </div>
  );
}

export default App;
